import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import * as FormDialog from "../../shared/FormDialog";
import type { CreateInformationTypePayload } from "../../api/informationTypes";
import { createInformationTypeMutationOptions } from "../../api/informationTypes";
import { useMutation } from "@tanstack/react-query";

type CreateInformationTypeDialogProps = {
  onClose: () => void;
  onCreated: (created: { id: string; name: string }) => void;
};

export const CreateInformationTypeDialog: FC<CreateInformationTypeDialogProps> = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const createMutation = useMutation(createInformationTypeMutationOptions());
  const { handleSubmit, reset, control, setFocus } = useForm<CreateInformationTypePayload>({
    defaultValues: { name: "" },
  });

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleCreate = ({ name }: { name: string }) =>
    createMutation.mutate(
      { name },
      {
        onSuccess: async ({ id }) => {
          createMutation.reset();
          onCreated({ id, name });
          handleCancel();
        },
      }
    );

  const handleCancel = () => {
    onClose();
    reset();
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("InformationType.Create.Dialog.Title")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleCreate)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={createMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("InformationType.Create.Dialog.Error") }}
              label={t("InformationType.Create.Dialog.NameLabel")}
              placeholder={t("InformationType.Create.Dialog.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleCancel} />
          <FormDialog.SubmitButton isSubmitting={createMutation.isPending}>
            {t("InformationType.Create.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
